import React, { useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  Table, TableBody, TableCell, TableRow
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { toWords } from 'number-to-words';

const PayslipModal = ({ open, onClose, data }) => {
  const printRef = useRef(null);

  if (!open || !data) return null;

  // Unpack payload
  const payroll = data.payroll || {};
  const master = data.master || data.employee || {};
  const employee = data.employee || data.master || {};
  const leaveBalance = data.leaveBalance || {};

  const handlePrint = () => {
    window.print();
  };

  // Safe formatting for numbers in scientific notation (e.g. 1.02083E+11) without undeclared BigInt
  const formatLongNumber = (val) => {
    if (!val || val === '-') return '-';
    const str = String(val).trim();
    if (str.toLowerCase().includes('e')) {
      const num = Number(str);
      if (!isNaN(num)) {
        if (typeof window !== 'undefined' && typeof window.BigInt === 'function') {
          return window.BigInt(Math.round(num)).toString();
        }
        return num.toLocaleString('fullwide', { useGrouping: false });
      }
    }
    return str;
  };

  const getField = (objA, objB, ...keys) => {
    for (const key of keys) {
      if (objA && objA[key] !== undefined && objA[key] !== null && String(objA[key]).trim() !== '') {
        return objA[key];
      }
      if (objB && objB[key] !== undefined && objB[key] !== null && String(objB[key]).trim() !== '') {
        return objB[key];
      }
    }
    return '-';
  };

  const getNumField = (objA, objB, ...keys) => {
    const val = getField(objA, objB, ...keys);
    const parsed = Number(val);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Employee Bio Information
  const empCode = getField(employee, payroll, 'empCode', 'EmpCode', 'code');
  const empName = getField(employee, payroll, 'Name', 'name', 'employeeName');
  const fatherName = getField(employee, master, 'FatherName', "Father's Name", 'fatherName');
  const designation = getField(employee, payroll, 'Designation', 'designation');
  const department = getField(employee, master, 'Department', 'department');
  const division = getField(employee, payroll, 'Division', 'division') || 'KOL';
  const project = getField(employee, payroll, 'projectCode', 'project', 'Project') || 'KTOF';
  const status = getField(employee, payroll, 'EmploymentType', 'employmentType') || 'Regular';
  const panNo = getField(employee, master, 'PANNo', 'panNo', 'pan');
  const pfNo = getField(employee, master, 'ProvidentFund', 'providentFund', 'pfNo');
  const fpfNo = getField(employee, master, 'PensionFund', 'pensionFund', 'fpfNo');

  // Formatted UAN and Aadhaar
  const uanNo = formatLongNumber(getField(employee, master, 'UANNo', 'UAN', 'uanNo'));
  const aadharNo = formatLongNumber(getField(employee, master, 'AadharNo', 'aadharNo', 'aadhaar'));

  const bankName = getField(employee, master, 'BankName', 'bankName');
  const bankAccountNo = getField(employee, master, 'BankAccountNo', 'bankAccountNo');
  const dojRaw = getField(employee, master, 'DOJ', 'doj', 'dateOfJoining');
  const formattedDoj = dojRaw !== '-' ? String(dojRaw).substring(0, 10) : '-';

  // Attendance Days & Proration Factor
  const workingDays = Number(payroll.workingDays || 30);
  const actualDays = Number(payroll.actualDays !== undefined ? payroll.actualDays : workingDays);
  const absentDays = Number(payroll.absentDays !== undefined ? payroll.absentDays : Math.max(0, workingDays - actualDays));
  const factor = workingDays > 0 ? (actualDays / workingDays) : 1;

  // Month & Year
  const monthNames = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
  ];
  const monthNum = Number(payroll.month) || (new Date().getMonth() + 1);
  const monthName = monthNames[monthNum - 1] || 'SEPTEMBER';
  const yearVal = payroll.year || new Date().getFullYear();

  // Earnings Breakdown
  const rawEarnings = [
    { label: 'Basic', gross: getNumField(master, payroll, 'BasicSalary', 'basicSalary', 'basic') },
    { label: 'HRA', gross: getNumField(master, payroll, 'HRA', 'hra') },
    { label: 'Leave Travel Allowance', gross: getNumField(master, payroll, 'LeaveTravelAllowance', 'leaveTravelAllowance', 'lta') },
    { label: 'Medical Allowance', gross: getNumField(master, payroll, 'MedicalAllowance', 'medicalAllowance', 'medical') },
    { label: 'Special Allowance', gross: getNumField(master, payroll, 'SpecialAllowance', 'specialAllowance') },
    { label: 'CCA', gross: getNumField(master, payroll, 'CCA', 'cca') },
    { label: 'Addl. Special Allowance', gross: getNumField(master, payroll, 'AddlSpecialAllowance', 'addlSpecialAllowance') },
    { label: 'Conveyance Allowance', gross: getNumField(master, payroll, 'ConveyanceAllowance', 'conveyanceAllowance') },
    { label: 'Project Allowance', gross: getNumField(master, payroll, 'ProjectAllowance', 'projectAllowance') },
    { label: 'Site Allowance', gross: getNumField(master, payroll, 'SiteAllowance', 'siteAllowance') },
    { label: 'Technical Allowance', gross: getNumField(master, payroll, 'TechnicalAllowance', 'technicalAllowance') },
    { label: 'Other Allowance', gross: getNumField(master, payroll, 'OtherAllowance', 'otherAllowance') },
  ];

  const earningsList = rawEarnings
    .filter(item => item.gross > 0 || ['Basic', 'HRA'].includes(item.label))
    .map(item => ({
      ...item,
      earned: Number((item.gross * factor).toFixed(2))
    }));

  const calculatedGross = earningsList.reduce((acc, curr) => acc + curr.gross, 0);
  const calculatedEarned = Number(earningsList.reduce((acc, curr) => acc + curr.earned, 0).toFixed(2));

  // Deductions: Prorate Statutory PF to sync row and total
  const masterPf = getNumField(master, payroll, 'eeStatutoryPf', 'PF');
  const basicSalaryVal = getNumField(master, payroll, 'BasicSalary', 'basicSalary', 'basic');
  const fullPf = masterPf > 0 ? masterPf : (basicSalaryVal > 0 ? Math.round(basicSalaryVal * 0.12) : 0);
  const earnedPf = Number((fullPf * factor).toFixed(2));

  const ptAmount = actualDays > 0
    ? (getNumField(master, payroll, 'ProfessionalTax', 'professionalTax') || 130)
    : 0;

  const deductionsList = [
    { label: 'Employee ESI', amount: getNumField(master, payroll, 'EmployeeESI', 'employeeEsi', 'esi') * factor },
    { label: 'Income Tax Deduction', amount: getNumField(master, payroll, 'itDeduction', 'tds') * factor },
    { label: 'Professional Tax', amount: ptAmount },
    { label: 'EE Voluntary PF Contribution', amount: getNumField(master, payroll, 'vpf', 'VPF') * factor },
    { label: 'EE Statutory PF Contribution', amount: earnedPf },
    { label: 'Other Recovery', amount: getNumField(payroll, master, 'otherRecovery') },
    { label: 'Society Recovery', amount: getNumField(payroll, master, 'societyRecovery') },
    { label: 'Mess Recovery', amount: getNumField(payroll, master, 'messRecovery') },
    { label: 'Misc. Recovery', amount: getNumField(payroll, master, 'miscRecovery') },
    { label: 'Advance', amount: getNumField(payroll, master, 'advance') },
  ].map(item => ({
    ...item,
    amount: item.amount > 0 ? Number(item.amount.toFixed(2)) : 0
  }));

  const calculatedDeductions = Number(deductionsList.reduce((acc, curr) => acc + curr.amount, 0).toFixed(2));

  // Synchronized Totals
  const totalGross = calculatedGross;
  const totalEarned = Number(payroll.totalEarnings) || calculatedEarned;
  const totalDeductions = Number(payroll.totalDeductions) || calculatedDeductions;

  const rawNet = totalEarned - totalDeductions;
  const netPayment = Number(payroll.netPayment) > 0 ? Number(payroll.netPayment) : Math.round(rawNet);
  const roundOff = Number((netPayment - rawNet).toFixed(2));

  const elBalance = getField(leaveBalance, master, 'elBalance', 'LimitofEarnedLeave') !== '-'
    ? getField(leaveBalance, master, 'elBalance', 'LimitofEarnedLeave')
    : '60';

  const getNetPayWords = () => {
    try {
      const roundedAmount = Math.round(netPayment);
      if (isNaN(roundedAmount) || roundedAmount <= 0) return 'Rupees Zero Only';
      const words = toWords(roundedAmount);
      return 'Rupees ' + words.replace(/\b\w/g, l => l.toUpperCase()) + ' Only';
    } catch (e) {
      return `Rupees ${netPayment.toLocaleString('en-IN')} Only`;
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 4, bgcolor: '#ffffff' }} id="printable-payslip" ref={printRef}>
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #printable-payslip, #printable-payslip * { visibility: visible; }
            #printable-payslip { position: absolute; left: 0; top: 0; width: 100%; padding: 0; margin: 0; }
            .no-print { display: none !important; }
          }
        `}</style>

        {/* Company Header */}
        <Box textAlign="center" mb={2}>
          <Box
            component="img"
            src="/logo.png"
            alt="GDCL Logo"
            sx={{ height: 55, mb: 1 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <Typography variant="h5" fontWeight="bold" sx={{ fontFamily: 'serif', letterSpacing: 0.5 }}>
            Gannon Dunkerley & Co., Ltd.
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            CIN- U51109MH1924PLC001107
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            RO: 86 A, Topsia Road (South), Haute Street, 7th Floor, Kolkata - 700046
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Tel: 033-71214563/64 | E-mail: kolkataced@gdcl.in
          </Typography>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ mt: 1, borderTop: '1px solid #000', borderBottom: '1px solid #000', py: 0.5 }}
          >
            SALARY SLIP FOR MONTH OF {monthName}-{yearVal}
          </Typography>
        </Box>

        {/* Employee Bio Metadata Table */}
        <Table size="small" sx={{ mb: 2, border: '1px solid #000', '& td': { py: 0.3, px: 1, fontSize: '0.78rem' } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Employee Code</TableCell>
              <TableCell sx={{ width: '28%' }}>{empCode}</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Employee PF No.</TableCell>
              <TableCell sx={{ width: '28%' }}>{pfNo}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Employee Name</TableCell>
              <TableCell>{empName}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Employee FPF No</TableCell>
              <TableCell>{fpfNo}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Father's Name</TableCell>
              <TableCell>{fatherName}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Employee PAN</TableCell>
              <TableCell>{panNo}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Division</TableCell>
              <TableCell>{division}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Bank Account No</TableCell>
              <TableCell>{bankAccountNo}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
              <TableCell>{project}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Bank Name</TableCell>
              <TableCell>{bankName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
              <TableCell>{department}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Adhaar Number</TableCell>
              <TableCell>{aadharNo}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell>{status}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>UAN</TableCell>
              <TableCell>{uanNo}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Designation</TableCell>
              <TableCell>{designation}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date of Joining</TableCell>
              <TableCell>{formattedDoj}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Actual Days</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{actualDays}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>LOP Days</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: absentDays > 0 ? 'red' : 'inherit' }}>
                {absentDays}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Earnings & Deductions Tables */}
        <Box sx={{ display: 'flex', border: '1px solid #000' }}>
          {/* Earnings */}
          <Box sx={{ width: '50%', borderRight: '1px solid #000' }}>
            <Box sx={{ bgcolor: '#f1f5f9', py: 0.5, px: 1, borderBottom: '1px solid #000' }}>
              <Typography variant="caption" fontWeight="bold">EARNINGS</Typography>
            </Box>
            <Table size="small" sx={{ '& td': { py: 0.3, px: 1, fontSize: '0.75rem' } }}>
              <TableBody>
                <TableRow sx={{ bgcolor: '#fafafa', '& td': { fontWeight: 'bold' } }}>
                  <TableCell>Salary/Allowances</TableCell>
                  <TableCell align="right">Gross (₹)</TableCell>
                  <TableCell align="right">Earned (₹)</TableCell>
                </TableRow>
                {earningsList.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell>{e.label}</TableCell>
                    <TableCell align="right">{e.gross.toFixed(2)}</TableCell>
                    <TableCell align="right">{e.earned.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ borderTop: '1px solid #000', '& td': { fontWeight: 'bold' } }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">{totalGross.toFixed(2)}</TableCell>
                  <TableCell align="right">{totalEarned.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Deductions */}
          <Box sx={{ width: '50%' }}>
            <Box sx={{ bgcolor: '#f1f5f9', py: 0.5, px: 1, borderBottom: '1px solid #000' }}>
              <Typography variant="caption" fontWeight="bold">DEDUCTIONS</Typography>
            </Box>
            <Table size="small" sx={{ '& td': { py: 0.3, px: 1, fontSize: '0.75rem' } }}>
              <TableBody>
                <TableRow sx={{ bgcolor: '#fafafa', '& td': { fontWeight: 'bold' } }}>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount (₹)</TableCell>
                </TableRow>
                {deductionsList.map((d, i) => (
                  <TableRow key={i}>
                    <TableCell>{d.label}</TableCell>
                    <TableCell align="right">{d.amount > 0 ? d.amount.toFixed(2) : ''}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ borderTop: '1px solid #000', '& td': { fontWeight: 'bold' } }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">{totalDeductions.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Leave Information & Projected Income Tax */}
        <Box sx={{ display: 'flex', border: '1px solid #000', borderTop: 'none' }}>
          <Box sx={{ width: '50%', borderRight: '1px solid #000', p: 1 }}>
            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>LEAVE INFORMATION</Typography>
            <Table size="small" sx={{ '& td': { py: 0.2, px: 0.5, fontSize: '0.73rem', border: 'none' } }}>
              <TableBody>
                <TableRow>
                  <TableCell>Earned Leave Balance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{elBalance}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Buffer Leave</TableCell>
                  <TableCell align="right">0.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ width: '50%', p: 1 }}>
            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>PROJECTED INCOME TAX</Typography>
            <Table size="small" sx={{ '& td': { py: 0.2, px: 0.5, fontSize: '0.73rem', border: 'none' } }}>
              <TableBody>
                <TableRow>
                  <TableCell>Total Income</TableCell>
                  <TableCell align="right">0.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Tax Payable</TableCell>
                  <TableCell align="right">0.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Net Pay Box */}
        <Box sx={{ border: '1px solid #000', borderTop: 'none', p: 1.5, bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Gross Earnings: ₹{totalEarned.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Deductions: ₹{totalDeductions.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Round Off: ₹{roundOff.toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                Net Pay: ₹{netPayment.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mt: 1, fontStyle: 'italic', fontSize: '0.85rem' }}>
            Net Pay (In Words): {getNetPayWords()}
          </Typography>
        </Box>

        <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
          This is a computer generated pay slip and does not require any signature
        </Typography>
      </DialogContent>

      <DialogActions className="no-print" sx={{ p: 2 }}>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
        >
          Print / Save PDF
        </Button>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayslipModal;